import { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../../api/core/apiClient";
import { getAuthToken } from "../../../lib/auth";

export const useActiveAgents = () => {
    const [isOnline, setIsOnline] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [hasError, setHasError] = useState(false);
    const lastPingRef = useRef<number>(0);
    const isPingingRef = useRef<boolean>(false);

    const activateAgent = useCallback(async (force = false) => {
        // Prevent concurrent requests
        if (isPingingRef.current) return;

        // Skip if not authenticated
        const token = getAuthToken();
        if (!token) return;

        const now = Date.now();
        // Throttle to at most once every 5 seconds unless forced
        if (!force && now - lastPingRef.current < 5000) return;

        isPingingRef.current = true;
        setIsConnecting(true);

        try {
            await apiClient.post("/auth/active_me", {});
            lastPingRef.current = Date.now();
            setIsOnline(true);
            setHasError(false);
        } catch (error) {
            console.error("Error activating agent via backend:", error);
            setHasError(true);
        } finally {
            setIsConnecting(false);
            isPingingRef.current = false;
        }
    }, []);

    // 1. Initial activation on mount
    useEffect(() => {
        activateAgent(true);
    }, [activateAgent]);

    // 2. Unthrottled background heartbeat via Web Worker + fallback setInterval (Intervalo 25 segundos)
    useEffect(() => {
        let worker: Worker | null = null;
        let fallbackInterval: NodeJS.Timeout | null = null;

        try {
            const workerBlob = new Blob([`
                let timer = null;
                self.onmessage = function(e) {
                    if (e.data === 'start') {
                        if (timer) clearInterval(timer);
                        timer = setInterval(function() {
                            self.postMessage('tick');
                        }, 25000); // Intervalo de 25 segundos
                    } else if (e.data === 'stop') {
                        if (timer) clearInterval(timer);
                    }
                };
            `], { type: 'application/javascript' });

            const workerUrl = URL.createObjectURL(workerBlob);
            worker = new Worker(workerUrl);

            worker.onmessage = (e) => {
                if (e.data === 'tick') {
                    activateAgent(false);
                }
            };

            worker.postMessage('start');

            return () => {
                if (worker) {
                    worker.postMessage('stop');
                    worker.terminate();
                }
                URL.revokeObjectURL(workerUrl);
            };
        } catch (e) {
            console.warn("Web Worker not supported for active_me heartbeat, using fallback interval:", e);
            fallbackInterval = setInterval(() => {
                activateAgent(false);
            }, 25000);

            return () => {
                if (fallbackInterval) clearInterval(fallbackInterval);
            };
        }
    }, [activateAgent]);

    // 3. Immediate ping on tab visibility change, window focus, network restore
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                activateAgent(true);
            }
        };

        const handleFocus = () => {
            activateAgent(true);
        };

        const handleOnline = () => {
            activateAgent(true);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('online', handleOnline);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
        };
    }, [activateAgent]);

    // 4. User activity pulse (if idle and user interacts, ping if >15s since last ping)
    useEffect(() => {
        const handleUserActivity = () => {
            const now = Date.now();
            if (now - lastPingRef.current > 15000) {
                activateAgent(false);
            }
        };

        window.addEventListener('mousemove', handleUserActivity, { passive: true });
        window.addEventListener('keydown', handleUserActivity, { passive: true });
        window.addEventListener('click', handleUserActivity, { passive: true });

        return () => {
            window.removeEventListener('mousemove', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('click', handleUserActivity);
        };
    }, [activateAgent]);

    return { isOnline, isConnecting, hasError, retryConnection: () => activateAgent(true) };
};
